/**
 * Responsibility: Implement narrator hybrid behavior for the ai layer.
 * Scope: Owns ai-layer concerns only; cross-layer orchestration is excluded.
 */
export type NarratorSeparator = {
  index: number
  length: number
}

function isSeparatorLine(text: string, index: number, length: number): boolean {
  const lineStart = text.lastIndexOf('\n', index - 1) + 1
  const before = text.slice(lineStart, index)
  if (before.trim().length !== 0) return false

  const sepText = text.slice(index, index + length)
  if (!/^={2,}$/.test(sepText)) return false

  const afterStart = index + length
  const nextNewline = text.indexOf('\n', afterStart)
  const afterLine = nextNewline === -1 ? text.slice(afterStart) : text.slice(afterStart, nextNewline)
  if (afterLine.trim().length !== 0) return false

  return true
}

export function locateNarratorSeparator(text: string): NarratorSeparator | null {
  const source = String(text ?? '')
  
  // 1. Explicit separator line (===)
  const matches = source.matchAll(/={2,}/g)
  for (const match of matches) {
    const index = match.index ?? -1
    const length = match[0]?.length ?? 0
    if (index < 0 || length < 2) continue
    if (isSeparatorLine(source, index, length)) {
      return { index, length }
    }
  }

  // 2. Implicit separator: JSON start on a new line
  const jsonLineMatch = source.match(/(?:^|\n)\s*\{/)
  if (jsonLineMatch) {
    const index = jsonLineMatch.index ?? -1
    if (index >= 0) {
      const braceIdx = source.indexOf('{', index)
      return { index: braceIdx, length: 0 }
    }
  }

  // 3. Fallback: Any JSON-like block starting with {"suggestions"
  // This handles cases where the AI appends JSON directly to prose without a newline.
  const suggestionsMatch = source.match(/\{"suggestions"/)
  if (suggestionsMatch) {
    const index = suggestionsMatch.index ?? -1
    if (index >= 0) {
      return { index, length: 0 }
    }
  }

  return null
}

export function splitNarratorHybridResponse(raw: string): { prose: string; structured: string } {
  const source = String(raw ?? '')
  const sep = locateNarratorSeparator(source)
  if (!sep) return { prose: source, structured: '' }
  return {
    prose: source.slice(0, sep.index),
    structured: source.slice(sep.index + sep.length),
  }
}
